// frontend/src/components/shared/LocalizationFallbackBanner.tsx
//
// DEC-2026-09 Phase 2 Step E (2026-05-04): localization fallback banner.
//
// Shown when a non-English user requests serendipity content but the backend
// has no rows in their language yet → falls back to EN. The banner sets user
// expectations: "Showing English ideas — personalized content in your language
// is being prepared."
//
// Rendering rules:
//   - lang === effectiveLang  → render null (data is in user's language; nothing to say)
//   - lang === 'en'           → render null (EN user has nothing to fall back from)
//   - otherwise               → render banner
//
// Design rationale (Sally / UX Expert):
//   - Severity: info (not warning) — fallback is normal for new locales
//   - Non-dismissible: message is contextual to current data, not the user
//   - No animation: informational, sidesteps useReducedMotion complexity
//   - role="status" + aria-live="polite": announces to screen readers without interruption

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

export interface LocalizationFallbackBannerProps {
  /** User's detected/preferred language (e.g. 'fr'). 2-letter ISO code. */
  lang: string;
  /** What was actually served (e.g. 'en'). 2-letter ISO code. */
  effectiveLang: string;
}

const LocalizationFallbackBanner: React.FC<LocalizationFallbackBannerProps> = ({
  lang,
  effectiveLang,
}) => {
  const { t } = useTranslation();

  // Self-guards: don't render unless we're actually showing fallback content.
  if (!lang || !effectiveLang) return null;
  if (lang === effectiveLang) return null;
  if (lang === 'en') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('localizationFallbackBannerAriaLabel', 'Localization notice')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        margin: '0 0 16px 0',
        borderRadius: '10px',
        background:
          'linear-gradient(90deg, rgba(59,130,246,0.08) 0%, rgba(168,85,247,0.06) 100%)',
        border: '1px solid rgba(59,130,246,0.30)',
        color: 'var(--text-primary)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: '28px',
          height: '28px',
          borderRadius: '8px',
          backgroundColor: 'rgba(59,130,246,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Globe size={16} style={{ color: 'var(--info-500, #3b82f6)' }} aria-hidden="true" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
          {t('localizationFallbackBannerTitle', 'Showing English ideas')}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {t(
            'localizationFallbackBannerBody',
            'Personalized content in your language is being prepared'
          )}
        </div>
      </div>
    </div>
  );
};

export default LocalizationFallbackBanner;
